import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { isValidState } from '@/lib/stateUtils';

interface OccupationData {
  soc: string;
  title: string;
  /** Optional; when omitted, UI falls back to SOC-group prose */
  description?: string;
  median_salary: number;
  employment: number;
  no_degree_required: boolean;
}

// How api-occupations reports a match, best first. Everything unknown (or a
// response from before the matcher shipped) sorts last.
const MATCH_TIERS = ['title_exact', 'alias_exact', 'title_fuzzy', 'alias_fuzzy'];

const matchTier = (matchedVia?: string | null): number => {
  const index = MATCH_TIERS.indexOf(matchedVia ?? '');
  return index >= 0 ? index : MATCH_TIERS.length;
};

export const useOccupationResolver = (occupationSlug: string) => {
  return useQuery<OccupationData>({
    queryKey: ['occupation-resolver', occupationSlug],
    queryFn: async (): Promise<OccupationData> => {
      console.log('[useOccupationResolver] Resolving slug:', occupationSlug);
      
      // Check if this is actually a state name (common user error)
      if (isValidState(occupationSlug)) {
        console.error('[useOccupationResolver] Detected state name as occupation slug:', occupationSlug);
        throw new Error(`Invalid occupation: "${occupationSlug}" appears to be a state name, not an occupation`);
      }
      
      // Check if this is a SOC code pattern (XX-XXXX format)
      const socCodePattern = /^(\d{2}-\d{4})(?:-.*)?$/;
      const socCodeMatch = occupationSlug.match(socCodePattern);
      const socCode = socCodeMatch ? socCodeMatch[1] : null;
      
      // If it's a SOC code, try to match by SOC code first
      if (socCode) {
        console.log('[useOccupationResolver] Detected SOC code pattern:', socCode);
        
        // Search using the SOC code
        const { data, error } = await supabase.functions.invoke('api-occupations', {
          body: {
            search: socCode,
            limit: 20,
            offset: 0,
            prioritize_specific: true
          }
        });

        if (!error && data?.occupations && data.occupations.length > 0) {
          // Try to find exact SOC code match
          const socCodeMatch = data.occupations.find((occ: unknown) => 
            occ.soc_code === socCode || occ.soc_code?.replace(/\./g, '-') === socCode
          );
          
          if (socCodeMatch) {
            console.log('[useOccupationResolver] Found exact SOC code match:', socCodeMatch.occ_title);
            return {
              soc: socCodeMatch.soc_code,
              title: socCodeMatch.occ_title,
              median_salary: socCodeMatch.median_salary,
              employment: socCodeMatch.employment,
              no_degree_required: socCodeMatch.no_degree_required
            };
          }
          
          // If only one result and it's a SOC code search, use it
          if (data.occupations.length === 1) {
            const singleResult = data.occupations[0];
            console.log('[useOccupationResolver] Found single result for SOC code:', singleResult.occ_title);
            return {
              soc: singleResult.soc_code,
              title: singleResult.occ_title,
              median_salary: singleResult.median_salary,
              employment: singleResult.employment,
              no_degree_required: singleResult.no_degree_required
            };
          }
        }
      }
      
      // Handle compound occupation titles (multiple occupations connected by "and")
      const isCompoundTitle = occupationSlug.includes('-and-');
      let searchTerms: string[] = [];
      
      if (isCompoundTitle) {
        console.log('[useOccupationResolver] Detected compound occupation title');
        // Split compound titles and search for individual parts
        const parts = occupationSlug.split('-and-').map(part => part.replace(/-/g, ' ').trim());
        searchTerms = [
          ...parts, // Individual occupation parts
          occupationSlug.replace(/-/g, ' '), // Full title as backup
        ];
      } else {
        // Regular search terms for single occupations
        const baseTerm = occupationSlug.replace(/-/g, ' ');
        searchTerms = [
          baseTerm, // "industrial organizational psychologists"
          baseTerm.replace(/\b\w/g, l => l.toUpperCase()), // Title case
          occupationSlug, // Keep original with hyphens
        ];
        
        // If slug contains numeric patterns that might be invalid (e.g., "construction-3-types"),
        // try removing them and searching for the base term
        const numericPattern = /-\d+(-[a-z]+)*$/i;
        if (numericPattern.test(occupationSlug) && !socCode) {
          const baseSlug = occupationSlug.replace(numericPattern, '');
          const baseTermFromSlug = baseSlug.replace(/-/g, ' ');
          searchTerms.push(
            baseTermFromSlug,
            baseTermFromSlug.replace(/\b\w/g, l => l.toUpperCase())
          );
          console.log('[useOccupationResolver] Detected numeric pattern, trying base term:', baseTermFromSlug);
        }
      }

      // Track all search results across attempts
      const allResults: unknown[] = [];

      for (const searchTerm of searchTerms) {
        console.log('[useOccupationResolver] Trying search term:', searchTerm);
        
        const { data, error } = await supabase.functions.invoke('api-occupations', {
          body: {
            search: searchTerm,
            limit: 20, // Increased limit for better matching
            offset: 0,
            prioritize_specific: true
          }
        });

        if (error) {
          console.error('[useOccupationResolver] Search error:', error);
          continue;
        }

        console.log('[useOccupationResolver] Search results:', data?.occupations?.length || 0);

        if (data?.occupations && data.occupations.length > 0) {
          // Add to all results, avoiding duplicates
          for (const occ of data.occupations) {
            if (!allResults.find(r => r.soc_code === occ.soc_code)) {
              allResults.push(occ);
            }
          }
          
          // If searching for a SOC code and we have exactly one result, use it
          if (socCode && data.occupations.length === 1) {
            const singleResult = data.occupations[0];
            console.log('[useOccupationResolver] Found single result for SOC code search:', singleResult.occ_title);
            return {
              soc: singleResult.soc_code,
              title: singleResult.occ_title,
              median_salary: singleResult.median_salary,
              employment: singleResult.employment,
              no_degree_required: singleResult.no_degree_required
            };
          }
        }

        // Try exact slug match first
        const exactMatch = data?.occupations?.find((occ: unknown) => 
          occ.slug === occupationSlug
        );

        if (exactMatch) {
          console.log('[useOccupationResolver] Found exact slug match:', exactMatch.occ_title);
          return {
            soc: exactMatch.soc_code,
            title: exactMatch.occ_title,
            median_salary: exactMatch.median_salary,
            employment: exactMatch.employment,
            no_degree_required: exactMatch.no_degree_required
          };
        }
        
        // Try SOC code match if we're looking for a SOC code
        if (socCode) {
          const socCodeMatch = data?.occupations?.find((occ: unknown) => 
            occ.soc_code === socCode || occ.soc_code?.replace(/\./g, '-') === socCode
          );
          
          if (socCodeMatch) {
            console.log('[useOccupationResolver] Found SOC code match:', socCodeMatch.occ_title);
            return {
              soc: socCodeMatch.soc_code,
              title: socCodeMatch.occ_title,
              median_salary: socCodeMatch.median_salary,
              employment: socCodeMatch.employment,
              no_degree_required: socCodeMatch.no_degree_required
            };
          }
        }

        // Try fuzzy matching - look for close title matches
        const fuzzyMatch = data?.occupations?.find((occ: unknown) => {
          const normalizedTitle = occ.occ_title.toLowerCase().replace(/[^a-z0-9]/g, '');
          const normalizedSlug = occupationSlug.toLowerCase().replace(/[^a-z0-9]/g, '');
          return normalizedTitle.includes(normalizedSlug) || normalizedSlug.includes(normalizedTitle);
        });

        if (fuzzyMatch) {
          console.log('[useOccupationResolver] Found fuzzy match:', fuzzyMatch.occ_title);
          return {
            soc: fuzzyMatch.soc_code,
            title: fuzzyMatch.occ_title,
            median_salary: fuzzyMatch.median_salary,
            employment: fuzzyMatch.employment,
            no_degree_required: fuzzyMatch.no_degree_required
          };
        }
      }

      // If we have accumulated results from multiple searches, try to find the best match
      if (allResults.length > 0) {
        // Try to find a match based on the first significant word(s) of the slug
        const slugWords = occupationSlug.split('-').filter(w => w && !/^\d+$/.test(w)); // Remove pure numbers
        if (slugWords.length > 0) {
          const primaryWord = slugWords[0];
          const matchByPrimaryWord = allResults.find((occ: unknown) => {
            const titleWords = occ.occ_title.toLowerCase().split(/\s+/);
            return titleWords.some(word => word.startsWith(primaryWord.toLowerCase()) || primaryWord.toLowerCase().startsWith(word));
          });

          if (matchByPrimaryWord) {
            console.log('[useOccupationResolver] Found match by primary word:', matchByPrimaryWord.occ_title);
            return {
              soc: matchByPrimaryWord.soc_code,
              title: matchByPrimaryWord.occ_title,
              median_salary: matchByPrimaryWord.median_salary,
              employment: matchByPrimaryWord.employment,
              no_degree_required: matchByPrimaryWord.no_degree_required
            };
          }

          // Fall back to the best-matched result. The API reports how each row
          // matched, so prefer an exact official-title hit over a fuzzy one
          // rather than trusting result order alone.
          const firstResult = [...allResults].sort(
            (a: unknown, b: unknown) => matchTier(a.matched_via) - matchTier(b.matched_via)
          )[0];
          console.log('[useOccupationResolver] Using best accumulated result:', firstResult.occ_title, firstResult.matched_via);
          return {
            soc: firstResult.soc_code,
            title: firstResult.occ_title,
            median_salary: firstResult.median_salary,
            employment: firstResult.employment,
            no_degree_required: firstResult.no_degree_required
          };
        }
      }

      // If no results found, throw error instead of fallback
      console.error('[useOccupationResolver] No occupation found for slug:', occupationSlug);
      throw new Error(`Occupation not found: ${occupationSlug}`);
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24 // 24 hours
  });
};